<script setup lang="ts">
import type { SpacingProp } from '@/types/Layout';
import type Carousel from '@/components/widgets/Carousel.vue';

type DisplayMode = 'list' | 'slide';

type Props = {
  button?: Button;
  items?: Game[];
  title?: string;
  icon?: string;
  type?: MediaType;
  isLoading?: boolean;
  isLoadingMoreGames?: boolean;
  displayMode?: DisplayMode;
  /**
   * Items per row.
   */
  perRow?: SpacingProp;
  /**
   * Space between items.
   */
  spaceBetween?: SpacingProp;
  isFirstChildBigger?: boolean;
  shouldNormalizeThumbnail?: boolean;
  providertitle?: {
    background?: string;
    borderRadius?: string;
    marginBottom?: string;
    paddingBottom?: any;
    paddingRight?: string;
  };
  showPayToday?: boolean;
};

const {
  button,
  items,
  title,
  icon,
  type = 'game',
  displayMode = 'list',
  isLoading,
  isLoadingMoreGames,
  perRow = [6, 4, 3],
  spaceBetween = [24, 16, 12],
  isFirstChildBigger,
  providertitle,
  showPayToday = false,
} = defineProps<Props>();

const { isMobile, getSpacingMeasurementFromPropList } = useLayout();
const { getLoadingStrategy } = useImageLoading();

const carouselRef = ref<InstanceType<typeof Carousel> | null>(null);

const isSlideMode = computed<boolean>(() => {
  return displayMode === 'slide';
});

const isGameMediaType = computed<boolean>(() => {
  return type === 'game';
});

const spaceBetweenItems = computed<number>(() => {
  return getSpacingMeasurementFromPropList(spaceBetween, 16);
});

const spaceBetweenInPixels = computed<string>(() => {
  return spaceBetweenItems.value + 'px';
});

const itemsPerRow = computed<number>(() => {
  return getSpacingMeasurementFromPropList(perRow, 6);
});

// Este componente NÃO está no sistema de tracking above fold
// Portanto, SEMPRE usa lazy loading (tudo 0)
const itemsAboveFold = computed<number>(() => {
  return 0; // TUDO LAZY
});

const isSliderFadeVisible = computed<boolean>(() => {
  return isMobile.value && !!items && items?.length > 3;
});

const swiperRefForHeader = computed(() => {
  return isSlideMode.value ? carouselRef.value : null;
});

// Prepare a deduplicated items list to ensure stable keys in TransitionGroup
const itemsArray = computed(() => (items || []) as any[]);

const dedupedItems = computed(() => {
  const arr = itemsArray.value || [];
  const seen = new Set<any>();
  const out: any[] = [];

  for (const it of arr) {
    const id = it?.id ?? JSON.stringify(it);

    if (seen.has(id)) continue;
    seen.add(id);
    out.push(it);
  }

  return out;
});
</script>

<template>
  <div :class="$style.listarGames">
    <template v-if="isLoading">
      <div :class="$style.title">
        <BaseCommonSkeleton
          v-if="title"
          :class="$style.skeletonTitle"
        />
        <BaseCommonSkeleton
          v-if="button"
          :class="$style.skeletonButton"
        />
      </div>

      <div
        :class="[
          $style.items,
          { [$style.isFirstChildBigger]: isFirstChildBigger },
        ]"
      >
        <BaseCommonSkeleton
          v-for="i in isSlideMode ? itemsPerRow : 24"
          :key="i"
          :class="$style.skeletonItem"
        />
      </div>
    </template>

    <template v-else>
      <PerfilOficialSwiperHeader
        v-if="title || isSlideMode"
        :icon-path="icon ? `home/${icon}` : undefined"
        :see-more-url="button?.to"
        :style="providertitle"
        :swiper-ref="swiperRefForHeader"
        :title="title"
      />

      <div
        v-if="isSlideMode"
        class="w-full"
      >
        <LazyWidgetsCarousel
          ref="carouselRef"
          :display="{
            itemsPerRow,
            spaceBetweenItems,
          }"
          :item-class="$style.carouselItem"
          :items="dedupedItems"
          :slider-fade="{ right: isSliderFadeVisible }"
        >
          <template #item="{ item, index }">
            <CasinoMediaApostatudo
              :class="[{ [$style.isGame]: isGameMediaType }]"
              :game="item"
              :index="index"
              :loading="getLoadingStrategy(index, itemsAboveFold)"
              :show-pay-today="showPayToday"
              :type="type"
            />
          </template>
        </LazyWidgetsCarousel>
      </div>

      <TransitionGroup
        v-else
        :class="[
          $style.items,
          { [$style.isFirstChildBigger]: isFirstChildBigger },
        ]"
        name="list"
        tag="div"
      >
        <CasinoMediaApostatudo
          v-for="(game, idx) in dedupedItems"
          :key="game.id"
          :class="[
            $style.mediaItem,
            { [$style.isGame]: isGameMediaType, [$style.newCard]: game._isNew },
          ]"
          :game="game"
          :index="idx"
          :loading="getLoadingStrategy(idx, itemsAboveFold)"
          :show-pay-today="showPayToday"
          :type="type"
        />

        <!-- Shimmer/card-like placeholders during load more (infinite scroll only) -->
        <template v-if="isLoadingMoreGames">
          <div
            v-for="index in isSlideMode
              ? itemsPerRow
              : Math.min(itemsPerRow * 2, 24)"
            :key="`skeleton-card-${index}`"
            :class="$style.skeletonCard"
          >
            <div :class="$style.skeletonImage" />
            <div :class="$style.skeletonLine" />
          </div>
        </template>
      </TransitionGroup>
    </template>
  </div>
</template>

<style module lang="postcss">
.listarGames {
  @apply flex flex-col select-none w-full;
}

.title {
  @apply flex justify-between items-center pb-1.5 md:pb-2;
}

.moreItemsButton {
  @apply ml-auto py-0 px-2 rounded-full no-underline text-xs flex items-center justify-end gap-1 bg-button-bg/15;

  span {
    @apply font-semibold text-button-bg text-xs text-[.6rem];
  }

  svg {
    @apply hidden;
  }
}

.items {
  @apply grid w-full;

  gap: v-bind(spaceBetweenInPixels);
  grid-template-columns: repeat(v-bind(itemsPerRow), minmax(0, 1fr));

  &.isFirstChildBigger {
    @media (min-width: 1024px) {
      a.mediaItem.isGame {
        &:nth-child(1) {
          grid-area: 1 / 1 / 3 / 3;
        }
      }
    }
  }
}

.skeletonTitle {
  @apply h-[20px] max-w-[200px] flex-1;
}

.skeletonButton {
  @apply h-[20px] max-w-[60px] flex-1;
}

.skeletonItem {
  @apply w-full h-auto aspect-[25/29];
}

.navigationButton {
  @apply hover:text-primary cursor-pointer text-texts/60;

  & + .navigationButton {
    @apply ml-3;
  }

  &.isNavigationButtonDisabled {
    @apply text-texts/20 cursor-not-allowed;
  }
}

.carouselItem {
  @media (max-width: 768px) {
    min-width: 87px;
    max-width: 87px;
    --card-image-aspect: 128.74%;
  }
}

.skeletonCard {
  @apply w-full h-auto aspect-[25/29] bg-[#0f1724] rounded overflow-hidden flex flex-col gap-2 p-2;
  position: relative;
}

.skeletonImage {
  @apply w-full rounded bg-gradient-to-r from-[#0f1724] via-[#111827] to-[#0f1724];
  flex: 1 1 auto;
  animation: shimmer 1.2s infinite linear;
}

.skeletonLine {
  @apply h-3 w-3/4 bg-gradient-to-r from-[#0f1724] via-[#111827] to-[#0f1724] rounded;
  animation: shimmer 1.2s infinite linear;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.newCard {
  animation: newCardPulse 600ms ease-out;
}

.placeholdersOverlay {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 5;
  background: transparent;
}

@keyframes newCardPulse {
  0% {
    transform: translateY(4px) scale(0.995);
    box-shadow: 0 0 0 0 rgba(0, 201, 255, 0);
  }
  50% {
    transform: translateY(0) scale(1.01);
    box-shadow: 0 6px 18px rgba(0, 201, 255, 0.06);
  }
  100% {
    transform: translateY(0) scale(1);
    box-shadow: none;
  }
}
</style>

<style lang="postcss">
/* Items fade in smoothly */
.list-enter-from {
  opacity: 0;
}
.list-enter-active {
  transition: opacity 300ms ease-out;
}
.list-leave-active {
  /* Fade out in-place instead of position:absolute which collapses grid height */
  opacity: 0;
  pointer-events: none;
  transition: opacity 150ms ease-out;
}
.list-leave-to {
  opacity: 0;
}
.list-move {
  transition: transform 200ms ease;
}
</style>
